/* eslint-disable spire/export-styles */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import requestExpertAssistData, { RequestExpertAssistParameter } from "@tCentro/Handlers/CENRequestExpertAssistHandler";
import * as React from "react";
import { css } from "styled-components";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { connect, ResolveThunks } from "react-redux";
import { getUrl } from "@insite/client-framework/ServerSideRendering";

const enum fields {
    title = "title",
    subtitle = "subtitle",
    description = "description",
    callRailNumber = "callRailNumber",
    availabilityText = "availabilityText",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.subtitle]: string;
        [fields.description]: string;
        [fields.callRailNumber]: string;
        [fields.availabilityText]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        expertAssist: getSettingsCollection(state).customSettings,
    };
};

const mapDispatchToProps = {
    requestExpertAssistData
};

type Props = OwnProps & HasProduct & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const EXPERT_ASSIST_SHOWN_PREFIX = "cen_expert_assist_shown_";
const EXPERT_ASSIST_SUBMITTED_PREFIX = "cen_expert_assist_submitted_";

const getProductStorageKey = (
    prefix: string,
    productNumber: string,
): string => {
    return `${prefix}${productNumber}`;
};

// US mobile/phone: 10 digits, optional +1/1 prefix, NANP rules
// (area code and exchange code can't start with 0 or 1)
const PHONE_REGEX = /^(?:\+1|1)?[2-9]\d{2}[2-9]\d{6}$/;

const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);

    if (digits.length <= 3) {
        return digits;
    }

    if (digits.length <= 6) {
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }

    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const validatePhone = (value: string) => {
    const digits = value.replace(/\D/g, "");

    if (!digits) return "Phone number is required";
    if (!PHONE_REGEX.test(digits)) return "Enter a valid 10-digit phone number";
    return "";
};

const getDataLayer = () => {
    return (window as any).dataLayer;
};


/*
 * ---------------------------------------------------------
 * CEN Expert Assist Styles
 * ---------------------------------------------------------
 */

interface CENExpertAssistStyles {
    overlay?: InjectableCss;
    modal?: InjectableCss;
    close?: InjectableCss;
    header?: InjectableCss;
    icon?: InjectableCss;
    title?: InjectableCss;
    subtitle?: InjectableCss;
    description?: InjectableCss;
    option?: InjectableCss;
    optionHeader?: InjectableCss;
    letter?: InjectableCss;
    optionTitle?: InjectableCss;
    optionDescription?: InjectableCss;
    phoneInput?: InjectableCss;
    phoneInputField?: InjectableCss;
    phoneInputError?: InjectableCss;
    primaryButton?: InjectableCss;
    note?: InjectableCss;
    or?: InjectableCss;
    orText?: InjectableCss;
    callLink?: InjectableCss;
    availability?: InjectableCss;
    availabilityIndicator?: InjectableCss;
    trust?: InjectableCss;
    trustItem?: InjectableCss;
    trustStrong?: InjectableCss;
    trustSmall?: InjectableCss;
    success?: InjectableCss;
    successIcon?: InjectableCss;
    successTitle?: InjectableCss;
    successDescription?: InjectableCss;
}

export const cenExpertAssistStyles: CENExpertAssistStyles = {
    overlay: {
        css: css`
            position: fixed;
            inset: 0;
            z-index: 99999;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 24px;
            background: rgba(0, 0, 0, 0.42);

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        align-items: flex-end;
                        padding: 0;
                    `,
                    css`
                        align-items: flex-end;
                        padding: 0;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },

    modal: {
        css: css`
            position: relative;
            width: 100%;
            max-width: 560px;
            max-height: calc(100vh - 48px);
            overflow-y: auto;
            padding: 28px;
            background: #ffffff;
            border-radius: 12px;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
            font-family: inherit;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 100%;
                        max-height: 92vh;
                        padding: 22px 18px;
                        border-radius: 16px 16px 0 0;
                    `,
                    css`
                        max-width: 100%;
                        max-height: 92vh;
                        padding: 22px 18px;
                        border-radius: 16px 16px 0 0;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },

    close: {
        css: css`
            position: absolute;
            top: 12px;
            right: 14px;
            width: 36px;
            height: 36px;
            border: 0;
            background: transparent;
            color: #444;
            font-size: 30px;
            line-height: 1;
            cursor: pointer;

            &:hover {
                color: #176b3a;
            }

            &:focus {
                outline: 2px solid #176b3a;
                outline-offset: 2px;
            }
        `,
    },

    header: {
        css: css`
            display: flex;
            gap: 14px;
            padding-right: 35px;
            margin-bottom: 20px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        gap: 10px;
                    `,
                    css`
                        gap: 10px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },

    icon: {
        css: css`
            width: 52px;
            height: 52px;
            flex: 0 0 52px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            background: #edf7f0;
            color: #176b3a;
            font-size: 24px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 44px;
                        height: 44px;
                        flex-basis: 44px;
                        font-size: 20px;
                    `,
                    css`
                        width: 44px;
                        height: 44px;
                        flex-basis: 44px;
                        font-size: 20px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },

    title: {
        css: css`
            margin: 0 0 7px;
            color: #222;
            font-size: 25px;
            line-height: 1.2;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 21px;
                    `,
                    css`
                        font-size: 21px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },

    subtitle: {
        css: css`
            font-size: 14px;
            font-weight: 600;
        `,
    },

    description: {
        css: css`
            margin: 5px 0 0;
            color: #666;
            font-size: 14px;
            line-height: 1.5;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 13px;
                    `,
                    css`
                        font-size: 13px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },

    option: {
        css: css`
            padding: 17px;
            border: 1px solid #c9ded0;
            border-radius: 8px;
            background: #f3faf5;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 15px;
                    `,
                    css`
                        padding: 15px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },

    optionHeader: {
        css: css`
            display: flex;
            gap: 12px;
            margin-bottom: 14px;
        `,
    },

    letter: {
        css: css`
            width: 34px;
            height: 34px;
            flex: 0 0 34px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            background: #176b3a;
            color: #fff;
            font-size: 14px;
            font-weight: 700;
        `,
    },

    optionTitle: {
        css: css`
            margin: 0 0 4px;
            color: #222;
            font-size: 17px;
        `,
    },

    optionDescription: {
        css: css`
            margin: 0;
            color: #666;
            font-size: 13px;
            line-height: 1.4;
        `,
    },

    phoneInput: {
        css: css`
            display: flex;
            height: 47px;
            margin-bottom: 10px;
            overflow: hidden;
            background: #fff;
            border: 1px solid #ccc;
            border-radius: 5px;

            &:focus-within {
                border-color: #176b3a;
            }
        `,
    },

    phoneInputField: {
        css: css`
            flex: 1;
            min-width: 0;
            width: 100%;
            padding: 0 12px;
            border: 0;
            outline: 0;
            font-size: 14px;

            &:focus {
                outline: none;
            }
        `,
    },
    phoneInputError: {
        css: css`
            display: block;
            margin-bottom: 6px;
            font-size: 12px;
            line-height: 16px;
            color: #EF4444;
            font-weight: 500;
        `,
    },

    primaryButton: {
        css: css`
            width: 100%;
            height: 45px;
            border: 0;
            border-radius: 5px;
            background: #176b3a;
            color: #fff;
            font-size: 14px;
            font-weight: 700;
            cursor: pointer;

            &:hover {
                background: #075c32;
            }

            &:focus {
                outline: 2px solid #176b3a;
                outline-offset: 2px;
            }

            &:disabled {
                opacity: 0.6;
                cursor: not-allowed;
            }
        `,
    },

    note: {
        css: css`
            margin-top: 9px;
            color: #666;
            font-size: 11px;
            line-height: 1.4;
        `,
    },

    or: {
        css: css`
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 38px;

            &:before {
                content: "";
                position: absolute;
                left: 0;
                right: 0;
                height: 1px;
                background: #ddd;
            }
        `,
    },

    orText: {
        css: css`
            position: relative;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #fff;
            border: 1px solid #ddd;
            border-radius: 50%;
            color: #555;
            font-size: 10px;
            font-weight: 700;
        `,
    },

    callLink: {
        css: css`
            display: flex;
            align-items: center;
            gap: 9px;
            margin-left: 46px;
            color: #176b3a;
            font-size: 22px;
            font-weight: 700;
            text-decoration: none;

            &:hover {
                text-decoration: underline;
            }

            &:focus {
                outline: 2px solid #176b3a;
                outline-offset: 2px;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 20px;
                    `,
                    css`
                        font-size: 20px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },

    availability: {
        css: css`
            margin-top: 5px;
            margin-left: 77px;
            color: #555;
            font-size: 12px;
        `,
    },

    availabilityIndicator: {
        css: css`
            display: inline-block;
            width: 7px;
            height: 7px;
            margin-right: 6px;
            border-radius: 50%;
            background: #2b9b52;
        `,
    },

    trust: {
        css: css`
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 15px;
            margin-top: 20px;
            padding-top: 17px;
            border-top: 1px solid #e5e5e5;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        grid-template-columns: 1fr;
                        gap: 10px;
                    `,
                    css`
                        grid-template-columns: 1fr;
                        gap: 10px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },

    trustItem: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },

    trustStrong: {
        css: css`
            color: #222;
            font-size: 11px;
        `,
    },

    trustSmall: {
        css: css`
            margin-top: 2px;
            color: #777;
            font-size: 10px;
        `,
    },

    success: {
        css: css`
            min-height: 350px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 30px;
            text-align: center;
        `,
    },

    successIcon: {
        css: css`
            width: 65px;
            height: 65px;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 2px solid #176b3a;
            border-radius: 50%;
            color: #176b3a;
            font-size: 30px;
            font-weight: 700;
        `,
    },

    successTitle: {
        css: css`
            margin: 15px 0 8px;
            font-size: 23px;
        `,
    },

    successDescription: {
        css: css`
            max-width: 340px;
            margin: 0 0 22px;
            color: #666;
            font-size: 14px;
            line-height: 1.5;
        `,
    },
};

const styles = cenExpertAssistStyles;

const CENPDPExpertAssist: React.FunctionComponent<Props> = ({
    product,
    expertAssist,
    requestExpertAssistData
}: Props) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [eip, setEip] = React.useState("");
    const [phone, setPhone] = React.useState("");
    const [phoneError, setPhoneError] = React.useState("");
    const [phoneTouched, setPhoneTouched] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [isSubmitted, setIsSubmitted] = React.useState(false);
    const url: { href: string; } = IS_SERVER_SIDE ? getUrl()! : window.location;
    const productNumber = product?.productNumber;
    const productTitle = product?.productTitle;
    const eip1TimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);


    React.useEffect(() => {
        if (product?.properties?.isExpertAssistRequired === "true") {

            const enableEIP1 = (expertAssist as any)?.enableEIP1;
            const eiP1DisplayDelaySeconds =
                (expertAssist as any)?.eiP1DisplayDelaySeconds;

            if (!enableEIP1 || !productNumber) {
                return;
            }

            const shownKey = getProductStorageKey(
                EXPERT_ASSIST_SHOWN_PREFIX,
                productNumber,
            );

            const isAlreadyShown =
                sessionStorage.getItem(shownKey) === "true";


            // Already shown for this product
            if (isAlreadyShown) {
                return;
            }

            // Prevent multiple timers for the same component
            if (eip1TimerRef.current) {
                return;
            }

            eip1TimerRef.current = setTimeout(() => {
                // Check storage again before opening
                const alreadyShown =
                    sessionStorage.getItem(shownKey) === "true";

                if (alreadyShown) {
                    eip1TimerRef.current = null;
                    return;
                }

                setIsOpen(true);
                setEip("eip1");
                trackEvent("pdp_eip_one_shown")
                sessionStorage.setItem(shownKey, "true");
                
                eip1TimerRef.current = null;
            }, eiP1DisplayDelaySeconds * 1000);

            return () => {
                if (eip1TimerRef.current) {
                    clearTimeout(eip1TimerRef.current);
                    eip1TimerRef.current = null;
                }
            };
        }
    }, [product?.properties?.isExpertAssistRequired, expertAssist, productNumber]);

    /*
     * ---------------------------------------------------------
     * GA4
     * ---------------------------------------------------------
     */

    const trackEvent = React.useCallback(
        (
            eventName: string,
            additionalData: Record<string, unknown> = {},
        ) => {
            if (typeof window === "undefined") {
                return;
            }

            const dataLayer = getDataLayer();

            if (!dataLayer) {
                return;
            }

            dataLayer.push({
                event: eventName,
                items: [
                    {
                        item_id: productNumber,
                        item_name: product?.productTitle,
                        ...additionalData,
                    }
                ]
            });
        },
        [productNumber],
    );

    /*
     * ---------------------------------------------------------
     * Popup visibility
     * ---------------------------------------------------------
     */

    const canShowPopup = React.useCallback(() => {
        if (
            typeof window === "undefined" ||
            !productNumber
        ) {
            return false;
        }

        const shownKey = getProductStorageKey(
            EXPERT_ASSIST_SHOWN_PREFIX,
            productNumber,
        );

        const submittedKey = getProductStorageKey(
            EXPERT_ASSIST_SUBMITTED_PREFIX,
            productNumber,
        );

        if (sessionStorage.getItem(shownKey)) {
            return false;
        }

        if (sessionStorage.getItem(submittedKey)) {
            return false;
        }

        return true;
    }, [productNumber]);

    /*
     * ---------------------------------------------------------
     * Show popup
     * ---------------------------------------------------------
     */

    const showPopup = React.useCallback(() => {
        const shownKey = getProductStorageKey(
            EXPERT_ASSIST_SHOWN_PREFIX,
            productNumber,
        );
        const isAlreadyShown =
            sessionStorage.getItem(shownKey) === "true";

        if (
            typeof window === "undefined" ||
            !productNumber ||
            !canShowPopup() && isAlreadyShown
        ) {
            return;
        }


        sessionStorage.setItem(
            shownKey,
            "true",
        );

        setIsOpen(true);
        setEip("eip2");

        trackEvent("pdp_eip_two_shown")
    }, [
        canShowPopup,
        productNumber,
        trackEvent,
    ]);



    /*
     * ---------------------------------------------------------
     * Desktop Exit Intent
     * ---------------------------------------------------------
     */

    React.useEffect(() => {
        const enableEIP2 = (expertAssist as any)?.enableEIP2;
        const eiP2DisplayDelaySeconds =
            (expertAssist as any)?.eiP2DisplayDelaySeconds;

        if (
            product?.properties?.isExpertAssistRequired !== "true" ||
            !enableEIP2
        ) {
            return;
        }

        let clickHandler: ((event: MouseEvent) => void) | null = null;
        let mouseLeaveHandler: ((event: MouseEvent) => void) | null = null;

        const timer = setTimeout(() => {
            clickHandler = (event: MouseEvent) => {
                const target = event.target as HTMLElement;

                // Allow cart click normally
                if (
                    target.closest(
                        '[data-test-selector="cartLink"]',
                    )
                ) {
                    return;
                }

                // Check if click is inside header or footer
                const header = target.closest("header");
                const footer = target.closest("footer");

                if (!header && !footer) {
                    return;
                }

                if (canShowPopup()) {
                    event.preventDefault();
                    event.stopPropagation();
                    showPopup();
                }
            };

            mouseLeaveHandler = (event: MouseEvent) => {
                if (event.clientY <= 20 && canShowPopup()) {
                    showPopup();
                }
            };

            // Capture phase ensures we catch the click before
            // header/footer navigation handlers.
            document.addEventListener(
                "click",
                clickHandler,
                true,
            );

            document.addEventListener(
                "mouseleave",
                mouseLeaveHandler,
            );
        }, eiP2DisplayDelaySeconds * 1000);

        return () => {
            clearTimeout(timer);

            if (clickHandler) {
                document.removeEventListener(
                    "click",
                    clickHandler,
                    true,
                );
            }

            if (mouseLeaveHandler) {
                document.removeEventListener(
                    "mouseleave",
                    mouseLeaveHandler,
                );
            }
        };
    }, [
        product?.properties?.isExpertAssistRequired,
        expertAssist,
        canShowPopup,
        productNumber,
        showPopup,
    ]);

    /*
     * ---------------------------------------------------------
     * Escape key
     * ---------------------------------------------------------
     */

    const closePopup = React.useCallback(() => {
        setIsOpen(false);
        if (isSubmitted) return
        eip === "eip1" ? trackEvent("pdp_eip_one_dismissed") : trackEvent("pdp_eip_two_dismissed");

    }, [trackEvent, isSubmitted]);

    React.useEffect(() => {
        if (
            typeof window === "undefined" ||
            !isOpen
        ) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                closePopup();
            }
        };

        document.addEventListener(
            "keydown",
            handleKeyDown,
        );

        return () => {
            document.removeEventListener(
                "keydown",
                handleKeyDown,
            );
        };
    }, [
        closePopup,
        isOpen,
    ]);

    /*
     * ---------------------------------------------------------
     * Prevent body scrolling while popup is open
     * ---------------------------------------------------------
     */

    React.useEffect(() => {
        if (
            typeof document === "undefined" ||
            !isOpen
        ) {
            return;
        }

        const originalOverflow =
            document.body.style.overflow;

        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow =
                originalOverflow;
        };
    }, [isOpen]);

    /*
     * ---------------------------------------------------------
     * Callback
     * ---------------------------------------------------------
     */

    const submitCallback = async (
        event: React.FormEvent<HTMLFormElement>,
    ) => {
        event.preventDefault();

        if (
            !phone.trim() ||
            !productNumber
        ) {
            return;
        }


        requestExpertAssistData({
            model: {
                phoneNumber: phone.replace(/\D/g, ""),
                pageType: "PDP",
                pageUrl: url.href,
                productName: productTitle ?? "",
                productErpNumber: productNumber ?? "",
                SubmittedFrom: eip === "eip1" ? "EIP1" : "EIP2",
            },
            onSuccess: (result: any) => {

                eip === "eip1" ? trackEvent("pdp_eip_one_submitted") : trackEvent("pdp_eip_two_submitted"); 

                setIsSubmitted(true);
            },
        })
    };

    /*
     * ---------------------------------------------------------
     * Call Now
     * ---------------------------------------------------------
     */

    const handleCallNow = () => {
        // trackEvent(
        //     "expert_popup_submitted",
        //     {
        //         method: "call",
        //     },
        // );
    };

    const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const formattedValue = formatPhoneNumber(event.target.value);
        setPhone(formattedValue);
        if (phoneTouched) {
            setPhoneError(validatePhone(formattedValue));
        }
    };

    const handlePhoneBlur = () => {
        setPhoneTouched(true);
        setPhoneError(validatePhone(phone));
    };

    /*
     * ---------------------------------------------------------
     * Render
     * ---------------------------------------------------------
     */

    if (product?.properties?.isExpertAssistRequired !== "true") null

    return (
        <StyledWrapper>
            {isOpen && (
                <StyledWrapper
                    {...styles.overlay}
                    onMouseDown={event => {
                        if (
                            event.target ===
                            event.currentTarget
                        ) {
                            closePopup();
                        }
                    }}
                >
                    <StyledWrapper
                        {...styles.modal}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="cen-expert-assist-title"
                    >
                        <StyledWrapper as="button"
                            type="button"
                            className="cen-expert-assist-close"
                            aria-label="Close"
                            onClick={closePopup}
                            css={styles.close?.css}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M6 18L18 6M6 6l12 12"></path></svg>
                        </StyledWrapper>

                        {!isSubmitted ? (
                            <>
                                <StyledWrapper
                                    {...styles.header}
                                >
                                    <StyledWrapper
                                        {...styles.icon}
                                    >
                                        <span>
                                            ☎
                                        </span>
                                    </StyledWrapper>

                                    <div>
                                        <StyledWrapper
                                            as="h2"
                                            css={
                                                styles
                                                    .title
                                                    ?.css
                                            }
                                        >
                                            {eip === "eip1" ? siteMessage('pdp_eip1_heading') : siteMessage('pdp_eip2_heading')}
                                        </StyledWrapper>

                                        <StyledWrapper as="div"
                                            css={
                                                styles
                                                    .subtitle
                                                    ?.css
                                            }
                                        >
                                            {eip === "eip1" ? siteMessage('pdp_eip1_subheading') : siteMessage('pdp_eip2_subheading')}
                                        </StyledWrapper>

                                        <StyledWrapper as="p"
                                            css={
                                                styles
                                                    .description
                                                    ?.css
                                            }
                                        >
                                            {eip === "eip1" ? siteMessage('pdp_eip1_description') : siteMessage('pdp_eip2_description')}
                                        </StyledWrapper>
                                    </div>
                                </StyledWrapper>

                                <StyledWrapper
                                    {...styles.option}
                                >
                                    <form
                                        onSubmit={
                                            submitCallback
                                        }
                                    >
                                        <StyledWrapper
                                            {...styles.optionHeader}
                                        >
                                            <StyledWrapper
                                                {...styles.letter}
                                            >
                                                A
                                            </StyledWrapper>

                                            <div>
                                                <StyledWrapper
                                                    as="h3"
                                                    css={
                                                        styles
                                                            .optionTitle
                                                            ?.css
                                                    }
                                                >
                                                    {eip === "eip1" ? siteMessage('pdp_eip1_option_a_title') : siteMessage('pdp_eip2_option_a_title')}
                                                </StyledWrapper>

                                                <StyledWrapper as="p"
                                                    css={
                                                        styles
                                                            .optionDescription
                                                            ?.css
                                                    }
                                                >
                                                    {eip === "eip1" ? siteMessage('pdp_eip1_option_a_description') : siteMessage('pdp_eip2_option_a_description')}
                                                </StyledWrapper>
                                            </div>
                                        </StyledWrapper>

                                        <StyledWrapper
                                            {...styles.phoneInput}
                                        >
                                            <StyledWrapper as="input"
                                                type="tel"
                                                value={phone}
                                                onChange={handlePhoneChange}
                                                onBlur={handlePhoneBlur}
                                                placeholder={`${eip === "eip1" ? siteMessage('pdp_eip1_mobile_number_placeholder') : siteMessage('pdp_eip2_mobile_number_placeholder')}`}
                                                aria-label="Mobile number"
                                                maxLength={12}
                                                required
                                                css={
                                                    styles
                                                        .phoneInputField
                                                        ?.css
                                                }
                                            />
                                        </StyledWrapper>

                                        {phoneError && (
                                            <StyledWrapper as="span"
                                                css={
                                                    styles
                                                        .phoneInputError
                                                        ?.css
                                                }>
                                                {phoneError}
                                            </StyledWrapper>
                                        )}

                                        <StyledWrapper
                                            as="button"
                                            type="submit"
                                            disabled={
                                                isSubmitting || !!validatePhone(phone)
                                            }

                                            css={
                                                styles
                                                    .primaryButton
                                                    ?.css
                                            }
                                        >
                                            {isSubmitting
                                                ? "Sending..."
                                                : eip === "eip1" ? siteMessage('pdp_eip1_callback_button') : siteMessage('pdp_eip2_callback_button')}
                                        </StyledWrapper>

                                        <StyledWrapper as="div"
                                            css={
                                                styles
                                                    .note
                                                    ?.css
                                            }
                                        >
                                            🔒 {eip === "eip1" ? siteMessage('pdp_eip1_callback_note') : siteMessage('pdp_eip2_callback_note')}
                                        </StyledWrapper>
                                    </form>
                                </StyledWrapper>

                                <StyledWrapper
                                    {...styles.or}
                                >
                                    <StyledWrapper as="span"
                                        css={
                                            styles
                                                .orText
                                                ?.css
                                        }
                                    >
                                        OR
                                    </StyledWrapper>
                                </StyledWrapper>

                                <StyledWrapper
                                    {...styles.option}
                                >
                                    <StyledWrapper
                                        {...styles.optionHeader}
                                    >
                                        <StyledWrapper
                                            {...styles.letter}
                                        >
                                            B
                                        </StyledWrapper>

                                        <StyledWrapper as="div">
                                            <StyledWrapper
                                                as="h3"
                                                css={
                                                    styles
                                                        .optionTitle
                                                        ?.css
                                                }
                                            >
                                                {eip === "eip1" ? siteMessage('pdp_eip1_option_b_title') : siteMessage('pdp_eip2_option_b_title')}
                                            </StyledWrapper>

                                            <StyledWrapper
                                                as="p"
                                                css={
                                                    styles
                                                        .optionDescription
                                                        ?.css
                                                }
                                            >
                                                {eip === "eip1" ? siteMessage('pdp_eip1_option_b_description') : siteMessage('pdp_eip2_option_b_description')}
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </StyledWrapper>

                                    <StyledWrapper as="a"
                                        css={
                                            styles
                                                .callLink
                                                ?.css
                                        }
                                        href={`tel:${eip === "eip1" ? siteMessage('pdp_eip1_call_back_number') : siteMessage('pdp_eip2_call_back_number')}`}
                                        onClick={
                                            handleCallNow
                                        }
                                    >
                                        <span>
                                            ☎
                                        </span>

                                        {eip === "eip1" ? siteMessage('pdp_eip1_call_back_number') : siteMessage('pdp_eip2_call_back_number')}
                                    </StyledWrapper>

                                    <StyledWrapper as="div"
                                        css={
                                            styles
                                                .availability
                                                ?.css
                                        }
                                    >
                                        <StyledWrapper as="span"
                                            css={
                                                styles
                                                    .availabilityIndicator
                                                    ?.css
                                            }
                                        />
                                        {eip === "eip1" ? siteMessage('pdp_eip1_availability') : siteMessage('pdp_eip2_availability')}
                                    </StyledWrapper>
                                </StyledWrapper>

                                <StyledWrapper
                                    {...styles.trust}
                                >
                                    <StyledWrapper
                                        {...styles.trustItem}
                                    >
                                        <StyledWrapper as="strong"
                                            css={
                                                styles
                                                    .trustStrong
                                                    ?.css
                                            }
                                        >
                                            ✓ {eip === "eip1" ? siteMessage('pdp_eip1_trust_no_1_title') : siteMessage('pdp_eip2_trust_no_1_title')}
                                        </StyledWrapper>

                                        <StyledWrapper as="small"
                                            css={
                                                styles
                                                    .trustSmall
                                                    ?.css
                                            }
                                        >
                                            {eip === "eip1" ? siteMessage('pdp_eip1_trust_no_1_description') : siteMessage('pdp_eip2_trust_no_1_description')}
                                        </StyledWrapper>
                                    </StyledWrapper>

                                    <StyledWrapper
                                        {...styles.trustItem}
                                    >
                                        <StyledWrapper as="strong"
                                            css={
                                                styles
                                                    .trustStrong
                                                    ?.css
                                            }
                                        >
                                            ✓ {eip === "eip1" ? siteMessage('pdp_eip1_trust_no_2_title') : siteMessage('pdp_eip2_trust_no_2_title')}
                                        </StyledWrapper>

                                        <StyledWrapper as="small"
                                            css={
                                                styles
                                                    .trustSmall
                                                    ?.css
                                            }
                                        >
                                            {eip === "eip1" ? siteMessage('pdp_eip1_trust_no_2_description') : siteMessage('pdp_eip2_trust_no_2_description')}
                                            solution.
                                        </StyledWrapper>
                                    </StyledWrapper>

                                    <StyledWrapper
                                        {...styles.trustItem}
                                    >
                                        <StyledWrapper as="strong"
                                            css={
                                                styles
                                                    .trustStrong
                                                    ?.css
                                            }
                                        >
                                            🔒 {eip === "eip1" ? siteMessage('pdp_eip1_trust_3_title') : siteMessage('pdp_eip2_trust_3_title')}
                                        </StyledWrapper>

                                        <StyledWrapper as="small"
                                            css={
                                                styles
                                                    .trustSmall
                                                    ?.css
                                            }
                                        >
                                            {eip === "eip1" ? siteMessage('pdp_eip1_trust_3_description') : siteMessage('pdp_eip2_trust_3_description')}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </>
                        ) : (
                            <StyledWrapper
                                {...styles.success}
                            >
                                <StyledWrapper
                                    {...styles.successIcon}
                                >
                                    ✓
                                </StyledWrapper>

                                <StyledWrapper as="h2"
                                    css={
                                        styles
                                            .successTitle
                                            ?.css
                                    }
                                >
                                    {eip === "eip1" ? siteMessage('pdp_eip1_success_heading') : siteMessage('pdp_eip2_success_heading')}
                                </StyledWrapper>

                                <StyledWrapper as="p"
                                    css={
                                        styles
                                            .successDescription
                                            ?.css
                                    }
                                >
                                    {eip === "eip1" ? siteMessage('pdp_eip1_success_description') : siteMessage('pdp_eip2_success_description')}
                                </StyledWrapper>

                                <StyledWrapper as="button"
                                    type="button"
                                    css={
                                        styles
                                            .primaryButton
                                            ?.css
                                    }
                                    onClick={() =>
                                        setIsOpen(false)
                                    }
                                >
                                    {eip === "eip1" ? siteMessage('pdp_eip1_continue_shopping') : siteMessage('pdp_eip2_continue_shopping')}
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProduct(CENPDPExpertAssist)),

    definition: {
        group: "Basic",
        icon: "heading",
    },
};

export default widgetModule;